import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalBackButton } from 'dibs-elements/exports/ModalBackButton';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { Link } from 'dibs-elements/exports/Link';
import { IntlContext, defineMessages, FormattedMessage } from 'dibs-react-intl';
import { connect } from 'react-redux';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink';

import actions from '../actions';
import BulkOperationResultDisplay from 'dibs-bulk-operation-result-display';
import tracking from '../tracking';
import { steps } from '../constants';
import { RequestShippingQuoteForm } from './RequestShippingQuoteForm';
import { RequestShippingSuccessContent } from './RequestShippingSuccessContent';
import { ShippingDetailsContainer } from '../ShippingDetails/ShippingDetailsContainer';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';

import css from '../styles.scss';

const messages = defineMessages({
    mutationError: {
        id: 'dc.requestShippingModal.mutation_error',
        defaultMessage:
            'There was an error submitting your request. Please refresh the page and try again.',
    },
});

const { QUOTE_REQUEST, REQUEST_SUCCESS, PRE_QUOTE_DISPLAY } = steps;

export class RequestShippingQuoteBody extends Component {
    constructor(props) {
        super(props);

        this.onSubmit = this.onSubmit.bind(this);
        this.onSuccess = this.onSuccess.bind(this);
        this.onFailure = this.onFailure.bind(this);
        this.setInitialStep = this.setInitialStep.bind(this);
        this.onRequestShippingQuoteFormLoad = this.onRequestShippingQuoteFormLoad.bind(this);
        this.onPreQuotesLoadComplete = this.onPreQuotesLoadComplete.bind(this);

        this.state = {
            bulkOperationResult: null,
            isSubmitting: false,
            hasLoggedIn: null,
            isCustomQuoteRequestSuccess: false,
        };
        this.setInitialStep();
    }

    componentDidUpdate(prevProps) {
        const { hasLoggedIn } = this.state;
        if (prevProps.viewer !== this.props.viewer && !hasLoggedIn) {
            const { modalKey, setOpenModalKey, clearState, userId, triggerUserRefetch } =
                this.props;
            clearState();
            triggerUserRefetch(userId);
            setOpenModalKey(modalKey);
            this.setInitialStep();
        }
    }

    setInitialStep() {
        const { setCurrentStep, viewer } = this.props;
        const quoteRequestStatus = viewer?.relevantShipping?.quoteRequestStatus;
        if (
            quoteRequestStatus === 'customQuoteRequested' ||
            this.state.isCustomQuoteRequestSuccess
        ) {
            setCurrentStep(REQUEST_SUCCESS);
        } else {
            setCurrentStep(PRE_QUOTE_DISPLAY);
        }
    }

    onSubmit(event) {
        const { trackingLocation, submitTrackingLocation } = this.props;
        // submitTrackingLocation is passed when the request modal is accessed through a cta in the prequotes modal
        tracking.trackSend({ trackingLocation: submitTrackingLocation || trackingLocation }, event);
        this.setState({ isSubmitting: true });
    }

    onSuccess(response) {
        const {
            onSuccess,
            onSuccessfulPreQuoteRequest,
            trackingLocation,
            submitTrackingLocation,
            setCurrentStep,
        } = this.props;
        // submitTrackingLocation is passed when the request modal is accessed through a cta in the prequotes modal
        tracking.trackSuccess({ trackingLocation: submitTrackingLocation || trackingLocation });
        setCurrentStep(REQUEST_SUCCESS);

        const shipmentQuoteRequest = response?.shipmentQuoteRequest || null;
        this.setState({
            bulkOperationResult: shipmentQuoteRequest,
            isSubmitting: false,
            isCustomQuoteRequestSuccess: true,
        });

        if (onSuccess) {
            onSuccess(shipmentQuoteRequest || {});
        }
        if (onSuccessfulPreQuoteRequest) {
            const { zipCode, country } =
                shipmentQuoteRequest?.conversation?.transaction?.shipment?.shippingAddress || {};
            onSuccessfulPreQuoteRequest({
                userZipCode: zipCode || '',
                userCountryCode: country || '',
            });
        }
    }

    onPreQuotesLoadComplete(input) {
        const { onSuccessfulPreQuoteRequest } = this.props;
        if (onSuccessfulPreQuoteRequest) {
            onSuccessfulPreQuoteRequest(input || {});
        }
    }

    onFailure() {
        const errorMessage = this.context.formatMessage(messages.mutationError);
        this.props.setErrorMessage({ errorMessage });
        this.setState({ isSubmitting: false });
    }

    onRequestShippingQuoteFormLoad() {
        //this fires when the form is loaded
        const {
            setCurrentStep,
            trackingLocation,
            userId,
            showAuthModal,
            showCompleteAccount,
            onClose,
        } = this.props;

        if (!userId) {
            onClose();
            showAuthModal({
                onSuccess: () => {
                    this.setState({ hasLoggedIn: true }, () => {
                        this.onRequestShippingQuoteFormLoad();
                    });
                },
            });
        } else if (isEmailOnly(document.cookie) && typeof showCompleteAccount === 'function') {
            onClose();
            showCompleteAccount();
        } else {
            tracking.trackClicked({ label: trackingLocation });
            setCurrentStep(QUOTE_REQUEST);
        }
    }

    render() {
        const { bulkOperationResult, isSubmitting } = this.state;
        const {
            viewer,
            currentStep,
            setCurrentStep,
            isMC,
            userId,
            itemId,
            bulkItemIds,
            conversationIds,
            pageContext,
            errorMessage,
            clearState,
            currency,
            hasQuote,
            stepsWithForcedBackButton,
            onBackButtonClick,
            itemQuantity,
        } = this.props;

        //custom quote and prequote checks
        const quoteRequestStatus = viewer?.relevantShipping?.quoteRequestStatus;

        let conversation;
        let user;
        let transaction;

        if (isMC) {
            conversation = viewer?.conversation;
            transaction = conversation?.transaction || {};
            user = conversation?.originalBuyer?.user;
        } else {
            conversation = viewer?.buyerItemConversation;
            transaction = viewer?.transactions?.edges?.[0]?.node || {};
            user = viewer?.user;
        }
        const item = viewer?.itemRead?.[0];
        const { itemIsOnHold, isSold, isListingUnlisted, seller, title: itemTitle } = item || {};
        const { serviceId: conversationId, id: conversationRelayId } = conversation || {};
        const {
            serviceId: transactionId,
            id: transactionRelayId,
            hasCompletedCheckout,
            isCanceled,
        } = transaction || {};

        const { id: userRelayId } = user || {};

        const dealerPk = this.props.dealerPk || seller?.serviceId;

        //enabler
        const isTrade = user?.isVerifiedTrade;
        const onlyTradeRequestsQuote = false;
        const blockRequest =
            (hasCompletedCheckout && !isCanceled) ||
            (!isTrade && onlyTradeRequestsQuote && !isMC) ||
            (itemIsOnHold && !isMC) ||
            isSold ||
            isListingUnlisted;

        if (blockRequest) {
            setCurrentStep(PRE_QUOTE_DISPLAY);
        }

        const shouldShowDetails = currentStep === PRE_QUOTE_DISPLAY && !isSubmitting;

        const shouldShowSuccess = currentStep === REQUEST_SUCCESS;
        const shouldShowSQR =
            (currentStep !== PRE_QUOTE_DISPLAY || isSubmitting) && currentStep !== REQUEST_SUCCESS;

        const isBulkRequest = !!(bulkItemIds && bulkItemIds.length);
        const shouldShowBulkOperationResultDisplay = shouldShowSuccess && isBulkRequest;
        const shouldShowIndividualRequestSuccess =
            shouldShowSuccess && !shouldShowBulkOperationResultDisplay;

        const isBackButtonForced = stepsWithForcedBackButton.some(step => step === currentStep);
        const showBackButton = isBackButtonForced
            ? isBackButtonForced
            : hasQuote &&
              currentStep !== PRE_QUOTE_DISPLAY &&
              currentStep !== REQUEST_SUCCESS &&
              quoteRequestStatus !== 'requestQuote';

        const handleBackClick = () => {
            clearState();
            if (isBackButtonForced) {
                onBackButtonClick();
            } else {
                setCurrentStep(PRE_QUOTE_DISPLAY);
            }
        };

        const successHeader = (
            <FormattedMessage
                id="dc.requestShippingModal.success_header"
                defaultMessage="Request Sent"
            />
        );

        const header = (
            <FormattedMessage
                id="dc.requestShippingModal.header"
                defaultMessage={`{isBulkRequest, select,
                    true {Request Shipping Quotes}
                    other {Request a Shipping Quote}
                }`}
                values={{ isBulkRequest }}
            />
        );

        const subheader = (
            <FormattedMessage
                id="dc.requestShippingModal.subheader"
                defaultMessage="Let the seller know about any specific delivery requirements so they can quote you the best option."
            />
        );

        const policyLinkComponent = (
            <Link href={getBuyerSupportLink('/en-us/articles/14516223858587/')}>
                <FormattedMessage
                    id="dc.requestShippingModal.policyLinkComponent"
                    defaultMessage="our policy"
                />
            </Link>
        );
        const policyLink = (
            <p className={css.policyLink}>
                <FormattedMessage
                    id="dc.requestShippingModal.policyLink"
                    defaultMessage={`Sharing contact information to purchase outside of 1stDibs is against {policyLinkComponent}.`}
                    values={{ policyLinkComponent }}
                />
            </p>
        );

        const barHeaderTitle = shouldShowSuccess ? successHeader : header;

        return (
            <div>
                {showBackButton && (
                    <ModalBackButton disabled={isSubmitting} onClick={handleBackClick} />
                )}
                {!viewer ? null : (
                    <Fragment>
                        {shouldShowDetails && (
                            <ShippingDetailsContainer
                                viewer={viewer}
                                currency={currency}
                                openQuoteRequestModal={this.onRequestShippingQuoteFormLoad}
                                quoteRequestStatus={quoteRequestStatus}
                                blockRequest={blockRequest}
                                onPreQuotesLoadComplete={this.onPreQuotesLoadComplete}
                                itemQuantity={itemQuantity}
                            />
                        )}
                        {(shouldShowSQR || shouldShowIndividualRequestSuccess) && (
                            <BarHeader title={barHeaderTitle} />
                        )}
                        {shouldShowSQR && (
                            <ModalBody>
                                <div className={css.subheader}>{subheader}</div>
                                {!!errorMessage && (
                                    <div data-tn="sqr-error" className={css.sqrError}>
                                        {errorMessage}
                                    </div>
                                )}
                                <RequestShippingQuoteForm
                                    viewer={viewer}
                                    user={user || null}
                                    userId={userId}
                                    itemId={itemId}
                                    bulkItemIds={bulkItemIds}
                                    conversationIds={conversationIds}
                                    isBulkRequest={isBulkRequest}
                                    isSubmitting={isSubmitting}
                                    itemTitle={itemTitle}
                                    conversationId={conversationId}
                                    dealerPk={dealerPk}
                                    transactionId={transactionId}
                                    conversationRelayId={conversationRelayId}
                                    transactionRelayId={transactionRelayId}
                                    userRelayId={userRelayId}
                                    pageContext={pageContext}
                                    copy={this.copy}
                                    onSubmit={this.onSubmit}
                                    onSuccess={this.onSuccess}
                                    onFailure={this.onFailure}
                                    openQuoteRequestModal={this.onRequestShippingQuoteFormLoad}
                                    closeModal={this.props.onClose}
                                >
                                    {policyLink}
                                </RequestShippingQuoteForm>
                            </ModalBody>
                        )}
                        {shouldShowIndividualRequestSuccess && (
                            <RequestShippingSuccessContent
                                isMC={isMC}
                                userId={userId}
                                seller={seller}
                            />
                        )}
                        {shouldShowBulkOperationResultDisplay && (
                            <BulkOperationResultDisplay
                                flowType="REQUEST_SHIPPING"
                                bulkOperationResult={bulkOperationResult}
                                conversations={bulkOperationResult?.conversations || null}
                                onClose={this.props.onClose}
                            />
                        )}
                    </Fragment>
                )}
            </div>
        );
    }
}

RequestShippingQuoteBody.propTypes = {
    isOpened: PropTypes.bool,
    viewer: PropTypes.object,
    relay: PropTypes.object,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    bulkItemIds: PropTypes.array,
    conversationIds: PropTypes.array,
    dealerPk: PropTypes.string,
    onRequestShippingQuoteFormLoad: PropTypes.func,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onSuccess: PropTypes.func,
    pageContext: PropTypes.string,
    trackingLocation: PropTypes.string,
    submitTrackingLocation: PropTypes.string,
    onShowQuoteRequest: PropTypes.func,
    closeShippingModal: PropTypes.func,
    modalKey: PropTypes.string,
    isMC: PropTypes.bool,
    currency: PropTypes.string,
    hasQuote: PropTypes.bool,
    showAuthModal: PropTypes.func,
    stepsWithForcedBackButton: PropTypes.array,
    onBackButtonClick: PropTypes.func,
    showCompleteAccount: PropTypes.func,
    triggerUserRefetch: PropTypes.func,
    onSuccessfulPreQuoteRequest: PropTypes.func,
    itemQuantity: PropTypes.number,

    // Redux

    currentStep: PropTypes.string,
    errorMessage: PropTypes.string,

    clearState: PropTypes.func,
    setOpenModalKey: PropTypes.func,
    setCurrentStep: PropTypes.func,
    setErrorMessage: PropTypes.func,
};

RequestShippingQuoteBody.defaultProps = {
    pageContext: 'DEALER_SHIPPING_REQUEST',
    setCurrentStep: () => {},
    stepsWithForcedBackButton: [],
    onBackButtonClick: () => {},
    onSuccess: () => {},
    triggerUserRefetch: () => {},
};

RequestShippingQuoteBody.contextType = IntlContext;

export default connect(
    ({ shippingQuoteModal }) => shippingQuoteModal,
    actions
)(
    createFragmentContainer(RequestShippingQuoteBody, {
        viewer: graphql`
            fragment RequestShippingQuoteBody_viewer on Viewer {
                user(userId: $userId) @include(if: $hasUserId) {
                    id
                    isVerifiedTrade
                    ...RequestShippingQuoteForm_user
                }
                itemRead(itemIds: [$itemId]) @include(if: $shouldFetchItem) {
                    ... on Item {
                        title
                        isSold
                        isListingUnlisted
                        seller {
                            serviceId
                            ...RequestShippingSuccessContent_seller
                        }
                        hasPreQuotedShipping
                        itemIsOnHold: isOnHold(buyerId: $userId)
                    }
                }
                buyerItemConversation(buyerId: $userId, itemId: $itemId)
                    @include(if: $shouldFetchConversation) {
                    id
                    serviceId
                }
                transactions(
                    first: 1
                    buyerId: $userIds
                    itemId: $itemIds
                    viewName: "mc.conversationTransaction"
                    sort: "createdDate desc"
                ) @include(if: $shouldFetchTransaction) {
                    edges {
                        node {
                            id
                            serviceId
                            isCanceled
                            hasCompletedCheckout
                            lastShipmentQuote {
                                status
                            }
                        }
                    }
                }
                conversation(conversationId: $conversationId) @include(if: $hasConversationId) {
                    transaction {
                        id
                        serviceId
                        isCanceled
                        hasCompletedCheckout
                    }
                    originalBuyer {
                        user {
                            ...RequestShippingQuoteForm_user
                        }
                    }
                }
                relevantShipping(buyerId: $userId, itemId: $itemId, quantity: $itemQuantity) {
                    quoteRequestStatus
                    shipmentQuote {
                        subTotalAmount {
                            amount
                        }
                        isCustom
                    }
                }
                ...ShippingDetailsContainer_viewer
                ...RequestShippingQuoteForm_viewer
            }
        `,
    })
);
